import React, { useState, useEffect } from 'react';
import { useFormBuilder } from '../../context/FormBuilderContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { RefreshCw, ChevronLeft, ChevronRight, User, FileText, ClipboardList } from 'lucide-react';

const ACTION_COLORS = {
  'CREATE_FORM': 'bg-green-100 text-green-700',
  'UPDATE_FORM': 'bg-blue-100 text-blue-700',
  'DELETE_FORM': 'bg-red-100 text-red-700',
  'CLONE_FORM': 'bg-purple-100 text-purple-700',
  'UPDATE_STATUS': 'bg-amber-100 text-amber-700',
  'EDIT_RESPONSE': 'bg-blue-100 text-blue-700',
  'DELETE_RESPONSE': 'bg-red-100 text-red-700'
};

const ENTITY_ICONS = {
  'form': FileText,
  'response': ClipboardList
};

const ALL_ACTIONS = [
  'CREATE_FORM', 'UPDATE_FORM', 'DELETE_FORM', 'CLONE_FORM', 
  'UPDATE_STATUS', 'EDIT_RESPONSE', 'DELETE_RESPONSE'
];

const FormBuilderAuditLogs = () => {
  const { fetchAuditLogs } = useFormBuilder();
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, page_size: 50, total: 0, total_pages: 0 });
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');

  useEffect(() => {
    loadLogs();
  }, [pagination.page, entityFilter, actionFilter]);

  const loadLogs = async () => {
    setLoading(true);
    const result = await fetchAuditLogs(
      pagination.page, 
      pagination.page_size, 
      entityFilter === 'all' ? null : entityFilter, 
      actionFilter === 'all' ? null : actionFilter
    );
    setLogs(result.logs || []);
    setPagination(prev => ({ ...prev, ...result.pagination }));
    setLoading(false);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const EntityIcon = ({ entity }) => {
    const Icon = ENTITY_ICONS[entity] || FileText;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Audit Logs</h2>
          <p className="text-slate-500">Track all form builder activities</p>
        </div>
        <Button variant="outline" onClick={loadLogs} data-testid="refresh-audit-logs">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <Select value={entityFilter} onValueChange={setEntityFilter}>
              <SelectTrigger className="w-[180px]" data-testid="filter-entity">
                <SelectValue placeholder="All Entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                <SelectItem value="form">Forms</SelectItem>
                <SelectItem value="response">Responses</SelectItem>
              </SelectContent>
            </Select>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-[180px]" data-testid="filter-action">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {ALL_ACTIONS.map(action => (
                  <SelectItem key={action} value={action}>
                    {action.replace(/_/g, ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[180px]">Timestamp</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No audit logs found
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log, idx) => (
                  <TableRow key={log.id || idx} data-testid={`audit-row-${idx}`}>
                    <TableCell className="text-sm text-slate-500">
                      {formatTimestamp(log.timestamp)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center">
                          <User className="w-3 h-3" />
                        </div>
                        <span className="text-sm">{log.user}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-700'}>
                        {log.action?.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <EntityIcon entity={log.entity} />
                        <span className="text-sm capitalize">{log.entity}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600 line-clamp-2">{log.details}</span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">
          Showing {logs.length} of {pagination.total} logs
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page === 1}
            onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm">
            Page {pagination.page} of {pagination.total_pages || 1}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page >= pagination.total_pages}
            onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FormBuilderAuditLogs;
